import PropTypes from "prop-types";
import React, { useEffect, useRef, useCallback, useState } from "react";
import FeatherIcon from "feather-icons-react";
import SimpleBar from "simplebar-react";
import { withTranslation } from "react-i18next";
import MetisMenu from "metismenujs";
import { Link, useLocation } from "react-router-dom";
import withRouter from "../../../src/components/Common/withRouter";
import usePermissions from "src/hooks/usePermissions";
import { getTickets } from "src/helpers/backend_helper";
import { ticketBus } from "src/helpers/ticketEventBus";
import { getAuthUser } from "src/helpers/auth";
import {useCrmSystems} from "../../hooks/useCrmSystems";

const OPEN_TICKET_COUNT_STORAGE_KEY_PREFIX = "sidebar-open-ticket-count";

const getOpenTicketCountStorageKey = (userId: number) =>
  `${OPEN_TICKET_COUNT_STORAGE_KEY_PREFIX}:${userId || 0}`;

const readStoredOpenTicketCount = (storageKey: string): number => {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

const writeStoredOpenTicketCount = (storageKey: string, count: number) => {
  try {
    localStorage.setItem(storageKey, String(Math.max(0, Math.trunc(count))));
  } catch {
    // ignore storage failures
  }
};

const SidebarContent = (props: any) => {
  const ref: any = useRef();
  const path = useLocation();
  const {hasPermission} = usePermissions();
  const openTicketCountRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authUserId = Number(getAuthUser()?.user_id || 0);
  const openTicketCountStorageKey = getOpenTicketCountStorageKey(authUserId);
  const [openTicketCount, setOpenTicketCount] = useState<number>(() =>
    readStoredOpenTicketCount(getOpenTicketCountStorageKey(Number(getAuthUser()?.user_id || 0)))
  );
  const canViewTickets = hasPermission("view-tickets");

  const activateParentDropdown = useCallback((item: HTMLElement) => {
    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) return false;

    item.classList.add("active");
    item.parentElement?.classList.add("mm-active");

    let subMenu = item.parentElement?.closest("ul");
    while (subMenu && subMenu !== sideMenu && sideMenu.contains(subMenu)) {
      subMenu.classList.add("mm-show");

      const parentLi = subMenu.parentElement;
      if (parentLi) {
        parentLi.classList.add("mm-active");
        parentLi.querySelector(":scope > a")?.classList.add("mm-active");
      }

      subMenu = parentLi?.parentElement?.closest("ul") ?? null;
    }

    scrollElement(item);
    return false;
  }, []);

  const removeActivation = useCallback(() => {
    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) return;

    sideMenu
        .querySelectorAll(".active, .mm-active")
        .forEach((el) => el.classList.remove("active", "mm-active"));
    sideMenu
        .querySelectorAll(".mm-show")
        .forEach((el) => el.classList.remove("mm-show"));
  }, []);

  const activeMenu = useCallback(() => {
    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) return;

    removeActivation();

    const items = Array.from(sideMenu.getElementsByTagName("a"));
    const matchingMenuItem = items.find((a) => {
      const href = a.getAttribute("href") || "";
      if (href === "#" || href.endsWith("/#") || a.classList.contains("has-arrow")) {
        return false;
      }
      return a.pathname === path.pathname;
    });

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [path.pathname, activateParentDropdown, removeActivation]);

  useEffect(() => {
    ref.current.recalculate();
  }, []);

  const persistOpenTicketCount = useCallback((count: number) => {
    const nextCount = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    setOpenTicketCount(nextCount);
    writeStoredOpenTicketCount(openTicketCountStorageKey, nextCount);
  }, [openTicketCountStorageKey]);

  const loadOpenTicketCount = useCallback(async () => {
    if (!canViewTickets) {
      return;
    }

    try {
      const response: any = await getTickets("", {
        page: 1,
        per_page: 1,
        status: "open",
      });
      persistOpenTicketCount(Number(response?.pagination?.total || 0));
    } catch {
      // keep cached value if refresh fails
    }
  }, [canViewTickets, persistOpenTicketCount]);

  const scheduleOpenTicketCountRefresh = useCallback(() => {
    if (!canViewTickets) {
      return;
    }

    if (openTicketCountRefreshTimeoutRef.current) {
      clearTimeout(openTicketCountRefreshTimeoutRef.current);
    }

    openTicketCountRefreshTimeoutRef.current = setTimeout(() => {
      openTicketCountRefreshTimeoutRef.current = null;
      void loadOpenTicketCount();
    }, 150);
  }, [canViewTickets, loadOpenTicketCount]);

  useEffect(() => {
    const menu = new MetisMenu("#side-menu");
    return () => menu.dispose?.();
  }, []);

  useEffect(() => {
    setOpenTicketCount(readStoredOpenTicketCount(openTicketCountStorageKey));
  }, [openTicketCountStorageKey]);

  useEffect(() => {
    void loadOpenTicketCount();
  }, [loadOpenTicketCount]);

  useEffect(() => {
    if (!canViewTickets) {
      return;
    }

    const unsubscribeCreated = ticketBus.on("ticket-created", () => {
      scheduleOpenTicketCountRefresh();
    });
    const unsubscribeUpdated = ticketBus.on("ticket-updated", () => {
      scheduleOpenTicketCountRefresh();
    });
    const unsubscribeAccepted = ticketBus.on("ticket-accepted", () => {
      scheduleOpenTicketCountRefresh();
    });
    const unsubscribeDeleted = ticketBus.on("ticket-deleted", () => {
      scheduleOpenTicketCountRefresh();
    });
    const unsubscribeCountRefresh = ticketBus.on("ticket-count-refresh", () => {
      scheduleOpenTicketCountRefresh();
    });
    const unsubscribeReconnected = ticketBus.on("ws-reconnected", () => {
      scheduleOpenTicketCountRefresh();
    });

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeAccepted();
      unsubscribeDeleted();
      unsubscribeCountRefresh();
      unsubscribeReconnected();
    };
  }, [canViewTickets, scheduleOpenTicketCountRefresh]);

  useEffect(() => {
    if (!canViewTickets) {
      return;
    }

    const handleWindowFocus = () => {
      scheduleOpenTicketCountRefresh();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleOpenTicketCountRefresh();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canViewTickets, scheduleOpenTicketCountRefresh]);

  useEffect(() => {
    return () => {
      if (openTicketCountRefreshTimeoutRef.current) {
        clearTimeout(openTicketCountRefreshTimeoutRef.current);
      }
    };
  }, []);

  function scrollElement(item: any) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  const {data: crmSystemsResponse, isLoading, isError} = useCrmSystems();
  const crmSystems = crmSystemsResponse?.data.toSorted((a, b) => a.id - b.id) ?? [];
  const showTrafficOrders = hasPermission('view-traffic-orders') && !isError && (isLoading || crmSystems.length > 0)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    activeMenu();
  }, [activeMenu, crmSystemsResponse]);

  return (
    <React.Fragment>
      <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")} </li>
            <li>
              {
                hasPermission("view-dashboard") &&
                <Link to="/dashboard" className="">
                  <FeatherIcon icon="home"/> <span>{props.t("Dashboard")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission("view-brokers") &&
                <Link to="/brokers">
                  <FeatherIcon icon="briefcase"/> <span>{props.t("Brokers")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission("view-teams") &&
                <Link to="/teams">
                  <FeatherIcon icon="users"/> <span>{props.t("Teams")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-spends') &&
                (
                  <Link to="/spends">
                    <FeatherIcon icon="grid"/> <span>{props.t("Spends")}</span>
                  </Link>
                )
              }
            </li>
            <li>
              {
                hasPermission('view-orders') &&
                (
                  <Link to="/orders">
                    <FeatherIcon icon="tool"/> <span>{props.t("Orders")}</span>
                  </Link>
                )
              }
            </li>
            <li>
              {
                hasPermission('view-users') &&
                (
                  <Link to="/users">
                    <FeatherIcon icon="user"/> <span>{props.t("Users")}</span>
                  </Link>
                )
              }
            </li>
            <li>
              {
                hasPermission('view-office') &&
                <Link to="/offices">
                  <FeatherIcon icon="hexagon"/> <span>{props.t("Offices")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-crm-system') &&
                <Link to="/crm-systems">
                  <FeatherIcon icon="cpu"/> <span>{props.t("CRM Systems")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-price-day') &&
                <Link to="/price-day">
                  <FeatherIcon icon="dollar-sign"/> <span>{props.t("Price day")}</span>
                </Link>
              }
            </li>
            {/*<li>
              {
                hasPermission('view-tracker') &&
                <Link to="/tracker-capa">
                  <FeatherIcon icon="activity"/> <span>{props.t("Tracker")}</span>
                </Link>
              }
            </li>*/}

            <li>
              {
                hasPermission('view-lead-logs') &&
                <Link to="/lead-dashboard">
                  <FeatherIcon icon="star"/> <span>{props.t("Lead Dashboard")}</span>
                </Link>
              }
            </li>
            <li>
              {
                canViewTickets &&
                <Link to="/tickets">
                  <FeatherIcon icon="message-square"/> <span>{props.t("Ticket System")}</span>
                  {openTicketCount > 0 && <span className="badge bg-danger rounded-pill ms-2">{openTicketCount}</span>}
                </Link>
              }
            </li>
            <li>
              {
                canViewTickets &&
                <Link to="/tickets/dashboard">
                  <FeatherIcon icon="bar-chart-2"/> <span>{props.t("Ticket Analytics")}</span>
                </Link>
              }
            </li>

            {/* --- */}
            <li className="menu-title">{props.t("Traffic Management")}</li>
            {/*<li>*/}
            {/*  {*/}
            {/*    //hasPermission('view-country-box') &&*/}
            {/*    <Link to="/statistic-traffic-orders">*/}
            {/*      <FeatherIcon icon="grid"/> <span>{props.t("Statistic")}</span>*/}
            {/*    </Link>*/}
            {/*  }*/}
            {/*</li>*/}
            {showTrafficOrders && (
                crmSystems.length === 1 && !isLoading ? (
                    <li>
                      <Link to={`/traffic-orders/${crmSystems[0].id}`}>
                        <FeatherIcon icon="grid"/> <span>{props.t("Traffic Orders")}</span>
                      </Link>
                    </li>
                ) : (
                    <li>
                      <Link to="#" className="has-arrow">
                        <FeatherIcon icon="grid"/> <span>{props.t("Traffic Orders")}</span>
                      </Link>
                      <ul>
                        {isLoading && (
                            <li className="mb-1"><span className="ps-1 text-muted">{props.t("Loading...")}</span></li>
                        )}
                        {crmSystems.map((crm) => (
                            <li key={crm.id}>
                              <Link to={`/traffic-orders/${crm.id}`} className="ps-0">
                                {crm.name}
                              </Link>
                            </li>
                        ))}
                      </ul>
                    </li>
                )
            )}
            <li>
              {
                hasPermission('view-common-orders') &&
                <Link to="/common-orders">
                  <FeatherIcon icon="grid"/> <span>{props.t("Common Orders")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-country-box') &&
                <Link to="/country-boxes">
                  <FeatherIcon icon="grid"/> <span>{props.t("Country Boxes")}</span>
                </Link>
              }
            </li>
            {/* --- */}

            {/* --- */}
            <li className="menu-title">{props.t("Track Management")}</li>
            <li>
              {
                hasPermission('view-echo-notifications') &&
                <Link to="/exo">
                  <FeatherIcon icon="cpu"/> <span>{props.t("EXO Notification")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-telegram-bots') &&
                <Link to="/tg-bots">
                  <FeatherIcon icon="cpu"/> <span>{props.t("Telegram Bots")}</span>
                </Link>
              }
            </li>
            {/* --- */}

            <li className="menu-title">{props.t("Elements")}</li>
            <li>
              {
                hasPermission('view-checkin') &&
                <Link to="/check-in">
                  <FeatherIcon icon="calendar"/> <span>{props.t("Checkin")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-jira') &&
                <Link to="/jira-statistic">
                  <FeatherIcon icon="calendar"/> <span>{props.t("Jira Statistic")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-jira') &&
                <Link to="/dns-checker">
                  <FeatherIcon icon="calendar"/> <span>{props.t("DNS checker")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-jira') &&
                <Link to="/kio-domain-changer">
                  <FeatherIcon icon="calendar"/> <span>{props.t("Keitaro Domain Changer")}</span>
                </Link>
              }
            </li>
            <li className="menu-title">{props.t("Managements")}</li>
            <li>
              {
                hasPermission('view-roles') &&
                <Link to="/roles">
                  <FeatherIcon icon="settings"/> <span>{props.t("Roles")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-permissions') &&
                <Link to="/permissions">
                  <FeatherIcon icon="tool"/> <span>{props.t("Permissions")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-crm-system') &&
                <Link to="/error-classifier">
                    <FeatherIcon icon="alert-triangle"/> <span>{props.t("Error Classifier")}</span>
                </Link>
              }
            </li>
            <li>
              {
                hasPermission('view-uni') &&
                <Link to="/uni">
                  <FeatherIcon icon="tool"/> <span>{props.t("Unicalizator")}</span>
                </Link>
              }
            </li>
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withTranslation()(withRouter(SidebarContent));
